/**
 * Öğretmen portalının sunucu istemcisi.
 *
 * Oturum httpOnly çerezle taşınıyor; jeton JavaScript'e verilmiyor.
 */

export class ApiHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    readonly ek?: Record<string, unknown>,
  ) {
    super(kod)
  }
}

async function istek<T>(yol: string, secenek: RequestInit = {}): Promise<T> {
  const r = await fetch(yol, {
    ...secenek,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', ...(secenek.headers ?? {}) },
  })
  const metin = await r.text()
  const govde = metin ? (JSON.parse(metin) as Record<string, unknown>) : {}
  if (!r.ok) throw new ApiHatasi(r.status, String(govde['hata'] ?? 'bilinmeyen'), govde)
  return govde as T
}

/**
 * Hangi kapıdan girildiği.
 *
 * `ogretmen.nirengi…` ve `rehber.nirengi…` AYNI uygulamayı sunuyor;
 * ayrı bir yığın kurmak iki yerde unutulacak kural demekti. Alt alan
 * adı yalnızca iki şeyi değiştiriyor: ekranın başlığı ve yanlış
 * adresten girene "sen öbür adresten giriyorsun" denebilmesi.
 *
 * Yerelde ikisini de denemek için `?kapi=rehber` çalışıyor.
 */
export function kapiyiBul(): Kapi {
  const p = new URL(location.href).searchParams.get('kapi')
  if (p === 'rehber' || p === 'counselor') return 'counselor'
  if (p === 'ogretmen' || p === 'teacher') return 'teacher'
  return location.hostname.split('.')[0] === 'rehber' ? 'counselor' : 'teacher'
}

export type Kapi = 'teacher' | 'counselor'

export const KAPI_ADI: Record<Kapi, string> = {
  teacher: 'Öğretmen',
  counselor: 'Rehber öğretmen',
}

export const api = {
  kodIste: (eposta: string) =>
    istek<{ gonderildi: boolean; maske: string; gecerlilikSn: number }>(
      '/api/ogretmen/kod-iste',
      { method: 'POST', body: JSON.stringify({ eposta }) },
    ),
  giris: (eposta: string, kod: string, kapi: Kapi) =>
    istek<{ ogretmen: Ogretmen }>('/api/ogretmen/giris', {
      method: 'POST',
      body: JSON.stringify({ eposta, kod, kapi }),
    }),
  cikis: () => istek<{ tamam: boolean }>('/api/ogretmen/cikis', { method: 'POST' }),
  ben: () => istek<{ ogretmen: Ogretmen }>('/api/ogretmen/ben'),

  ogrenciler: () => istek<{ ogrenciler: Ogrenci[] }>('/api/ogretmen/ogrenciler'),
  formlar: (danisanId?: string) =>
    istek<{ formlar: FormIstegi[]; tanimlar: Record<string, FormTanimi>; surum: string }>(
      `/api/ogretmen/formlar${danisanId ? `?danisanId=${danisanId}` : ''}`,
    ),
  gecmis: (danisanId?: string) =>
    istek<{ gecmis: Gecmis[] }>(
      `/api/ogretmen/gecmis${danisanId ? `?danisanId=${danisanId}` : ''}`,
    ),
  gonder: (istekId: string, cevaplar: Record<string, number>) =>
    istek<{ tamam: boolean }>(`/api/ogretmen/formlar/${istekId}`, {
      method: 'POST',
      body: JSON.stringify({ cevaplar }),
    }),

  davranislar: () =>
    istek<{ hedefler: DavranisHedefi[]; buHafta: string }>('/api/ogretmen/davranislar'),
  davranisYaz: (hedefId: string, hafta: string, deger: number, not?: string) =>
    istek<{ kaydedildi: boolean }>(`/api/ogretmen/davranis/${hedefId}`, {
      method: 'PUT',
      body: JSON.stringify({ hafta, deger, not }),
    }),
}

/**
 * Öğretmenin dolduracağı davranış hedefi.
 *
 * Başlangıç (baseline) DÖNMÜYOR: klinik bir karşılaştırma noktası ve
 * öğretmene tek başına bir şey söylemiyor.
 *
 * Başka kaynakların (veli, uygulayıcı) aynı hafta için girdiği sayı
 * da dönmüyor: görmek, tarafları birbirinin cevabına göre
 * ayarlamaya iter.
 */
export interface DavranisHedefi {
  id: string
  danisanId: string
  metin: string
  birim: string
  yon: 'lower' | 'higher'
  hedef: number | null
  kayitlar: { hafta: string; deger: number; not: string | null }[]
}

/* ------------------------------------------------------------------ */

export interface Ogretmen {
  adSoyad: string
  tur: Kapi
}

export interface Ogrenci {
  id: string
  ad: string
  soyad: string
  /** Psikologun BU öğretmene yazdığı not */
  not: string | null
  notTarih: string | null
  bekleyen: number
}

export interface FormIstegi {
  id: string
  danisanId: string
  ad: string
  soyad: string
  kod: string
  istendi: string
  bitis: string | null
  not: string | null
}

export interface Gecmis {
  id: string
  danisanId: string
  kod: string
  tarih: string
}

export interface FormTanimi {
  kod: string
  ad: string
  aciklama: string
  secenekler: string[]
  altBasliklar: Record<string, string>
  maddeler: { anahtar: string; metin: string; alt: string; ters?: boolean }[]
}

export const tarih = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'
